import { cellCenter } from '../grid.ts';
import { idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngChance, rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc, StrutsRules, StrutsState } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { shootTick } from './workshop.ts';

/**
 * The night of the promise (docs/sendas/artifice.md, chapter 4, act 1, the memory, played as young Nadd; the ground in
 * battle/ojos-layout.ts). Grada's ledge in the Marea of the Thaw, fifty-five years back: the ice broke the great stair,
 * and it hangs from four loose struts. Bruni, Nadd's apprentice, came down from the forge with two old golems without
 * asking the clan.
 *
 * A tap on a strut (`strutOrder`) sends a golem there, the nearer free one: it walks to it, sets its back to it (`set`)
 * and holds; Bruni comes behind and wedges it (`caulk`, 20 s), and the golem is free again. A golem struck while it
 * sets or holds lets go: the stair shakes (`sway` of the hero's life, wherever it stands; once a `swayGap` at most),
 * Bruni's wedging there goes `lossBp` back, and it sets its back again `back` later. Felled (its blows: the act's
 * people), Bruni goes and lights it again where it lies (`light`, 15 s). The Marea comes over the rim in bands, a
 * nightmare more every `grow`: some go for the golems (the common hunt), the rest for the houses (the hero in their
 * way: they fight it close by). One at a doorstep is in a house: `houses` of them and the night is lost. The quarrymen
 * at their doors strike what comes (`pick`). Grillo's lamp shows the loosest strut not yet wedged (he stands by it).
 * The stair groans now and then (`groan`, warned 3 s) and sheds stone over the whole ledge: more the more struts are
 * loose (neither held nor wedged); nowhere on the ledge is out of it, so the struts held are the only cover.
 * Won with the four wedged; lost with `houses` in or young Nadd down.
 *
 * Cost: two golems, Bruni and Grillo walked straight across the open ledge; the house-goers' list is short.
 */

const GOLEMS = ['golem1', 'golem2'];

const fresh = (r: StrutsRules, s: BattleState): StrutsState => ({
  st: r.struts.map(() => 0),
  prog: r.struts.map(() => 0),
  golems: GOLEMS.map((id) => ({ strut: -1, mode: 0, t: 0, hp: s.senda?.npcs.find((n) => n.id === id)?.hp ?? 0 })),
  job: -1,
  held: 0,
  leaks: 0,
  foe: r.foes.first,
  bands: 0,
  sway: -1000,
  point: r.creak[0] ?? 0,
  aims: [],
  end: -1,
  hurt: false,
  picks: [0, 0],
  pend: [],
  groan: r.groan.first,
  groanAt: -1,
});

const npc = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);

/** Where a golem stands to hold a strut (its back to it, a little south), where Bruni wedges it (beside). */
export const strutAt = (s: BattleState, r: StrutsRules, i: number): { x: number; y: number } => {
  const c = cellCenter(s.map.radius, r.struts[i] as number);
  return { x: c.x, y: c.y + 350 };
};
const wedgeAt = (s: BattleState, r: StrutsRules, i: number): { x: number; y: number } => {
  const c = cellCenter(s.map.radius, r.struts[i] as number);
  return { x: c.x + 550, y: c.y + 150 };
};

/** The golem going to or holding a strut (−1: none). */
export const strutGolem = (w: StrutsState, i: number): number => w.golems.findIndex((g) => g.strut === i && g.mode >= 1 && g.mode <= 3);

/** A tap on a strut: the nearer free golem goes to it (a golem going elsewhere is sent here instead if none is free). */
export const strutOrder = (s: BattleState, _h: Hero, at: number): void => {
  const r = s.rules?.senda?.struts;
  const w = s.senda?.struts;
  if (!r || !w || w.end >= 0 || at < 0 || at >= r.struts.length || w.st[at] !== 0 || strutGolem(w, at) >= 0) return;
  const p = strutAt(s, r, at);
  let best = -1;
  let bd = 0x7fffffff;
  for (const free of [true, false]) {
    w.golems.forEach((g, i) => {
      const n = npc(s, GOLEMS[i] as string);
      if (!n || n.hp <= 0 || (free ? g.mode !== 0 : g.mode !== 1)) return;
      const d = (n.x > p.x ? n.x - p.x : p.x - n.x) + (n.y > p.y ? n.y - p.y : p.y - n.y);
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    if (best >= 0) break;
  }
  if (best < 0) return;
  const g = w.golems[best] as typeof w.golems[number];
  g.strut = at;
  g.mode = 1;
  ev(s, { t: 'senda', kind: 'golemGo', npc: GOLEMS[best] as string, x: p.x, y: p.y, n: at });
};

/** The stair shakes: the hero struck wherever it stands (once a `swayGap` at most). */
const sway = (s: BattleState, r: StrutsRules, w: StrutsState, i: number): void => {
  if (s.tick - w.sway < r.swayGap) return;
  w.sway = s.tick;
  const c = cellCenter(s.map.radius, r.struts[i] as number);
  ev(s, { t: 'senda', kind: 'sway', npc: '', x: c.x, y: c.y, n: i });
  for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, r.sway), null, 'physical', true);
};

const golemsTick = (s: BattleState, r: StrutsRules, w: StrutsState, cue: (c: string) => void): void => {
  w.golems.forEach((g, i) => {
    const n = npc(s, GOLEMS[i] as string);
    if (!n) return;
    const struck = n.hp < g.hp;
    g.hp = n.hp;
    if (n.hp <= 0) {
      if (g.mode !== 4) {
        if (g.strut >= 0 && w.st[g.strut] === 1) w.st[g.strut] = 0;
        if (g.strut >= 0 && (g.mode === 2 || g.mode === 3)) sway(s, r, w, g.strut);
        g.strut = -1;
        g.mode = 4;
        ev(s, { t: 'senda', kind: 'golemDown', npc: n.id, x: n.x, y: n.y });
        cue('golemDown');
      }
      return;
    }
    if (struck && (g.mode === 2 || g.mode === 3) && g.strut >= 0) {
      // It lets go: the stair shakes, Bruni's wedging there goes back, it sets its back again in a moment.
      const j = g.strut;
      w.prog[j] = (w.prog[j] as number) - mulBp(w.prog[j] as number, r.lossBp);
      if (w.st[j] === 1) w.st[j] = 0;
      g.mode = 2;
      g.t = s.tick + r.golem.back;
      sway(s, r, w, j);
      ev(s, { t: 'senda', kind: 'golemLet', npc: n.id, x: n.x, y: n.y, n: j });
      cue('drop1');
      return;
    }
    n.moving = false;
    if (g.mode === 1) {
      const p = strutAt(s, r, g.strut);
      n.moving = true;
      if (stepToward(n, p.x, p.y, r.golem.pace)) {
        n.moving = false;
        n.facing = 768;
        g.mode = 2;
        g.t = s.tick + r.golem.set;
      }
    } else if (g.mode === 2 && s.tick >= g.t) {
      g.mode = 3;
      w.st[g.strut] = 1;
      const p = strutAt(s, r, g.strut);
      ev(s, { t: 'senda', kind: 'golemSet', npc: n.id, x: p.x, y: p.y, n: g.strut });
    }
  });
};

/** Bruni: lights a golem down (first), else wedges a strut a golem holds (the furthest on first). */
const bruniTick = (s: BattleState, r: StrutsRules, w: StrutsState, cue: (c: string) => void): void => {
  const b = npc(s, 'bruni');
  if (!b) return;
  // A job gone (the golem lit by now, the strut let go and left): another.
  if (w.job >= 10 && (w.golems[w.job - 10]?.mode ?? 0) !== 4) w.job = -1;
  if (w.job >= 0 && w.job < 10 && (w.st[w.job] === 2 || strutGolem(w, w.job) < 0)) w.job = -1;
  if (w.job < 0) {
    w.held = 0;
    const down = w.golems.findIndex((g) => g.mode === 4);
    if (down >= 0) w.job = 10 + down;
    else {
      let best = -1;
      for (let i = 0; i < w.st.length; i++) {
        if (w.st[i] !== 1) continue;
        if (best < 0 || (w.prog[i] as number) > (w.prog[best] as number)) best = i;
      }
      w.job = best;
    }
  }
  b.moving = false;
  if (w.job < 0) return;
  const at = w.job >= 10 ? npc(s, GOLEMS[w.job - 10] as string) : null;
  const p = at ? { x: at.x + 450, y: at.y + 200 } : wedgeAt(s, r, w.job);
  if (!withinRange(b.x, b.y, p.x, p.y, 120)) {
    b.moving = true;
    stepToward(b, p.x, p.y, r.bruni.pace);
    return;
  }
  b.facing = 512;
  if (w.job >= 10) {
    const i = w.job - 10;
    const g = w.golems[i];
    const n = npc(s, GOLEMS[i] as string);
    if (!g || !n) return;
    if (++w.held < r.light) return;
    n.hp = n.maxHp;
    g.hp = n.hp;
    g.mode = 0;
    w.job = -1;
    w.held = 0;
    ev(s, { t: 'senda', kind: 'golemLit', npc: n.id, x: n.x, y: n.y });
    return;
  }
  const j = w.job;
  const gi = strutGolem(w, j);
  const g = w.golems[gi];
  // Wedging only while the golem holds it (it lets go when struck: Bruni waits).
  if (!g || g.mode !== 3) return;
  w.prog[j] = (w.prog[j] as number) + 1;
  if ((w.prog[j] as number) < r.caulk) return;
  w.st[j] = 2;
  g.strut = -1;
  g.mode = 0;
  w.job = -1;
  const done = w.st.filter((x) => x === 2).length;
  const c = cellCenter(s.map.radius, r.struts[j] as number);
  ev(s, { t: 'senda', kind: 'wedged', npc: 'bruni', x: c.x, y: c.y, n: j });
  cue(`caulk${done}`);
  if (done >= r.struts.length) {
    w.end = s.tick;
    ev(s, { t: 'senda', kind: 'free', npc: 'bruni', x: c.x, y: c.y });
  }
};

/** Grillo by the loosest strut not yet wedged, his lamp on it. */
const grilloTick = (s: BattleState, r: StrutsRules, w: StrutsState, cue: (c: string) => void): void => {
  const g = npc(s, 'grillo');
  if (!g) return;
  const next = r.creak.find((i) => w.st[i] !== 2);
  w.point = next ?? -1;
  g.moving = false;
  if (next === undefined) return;
  const c = cellCenter(s.map.radius, r.struts[next] as number);
  const p = { x: c.x - 600, y: c.y + 900 };
  if (stepToward(g, p.x, p.y, 80)) {
    g.facing = iatanUp;
    if (s.waveT >= 24) cue('point');
  } else g.moving = true;
};
/** Facing up (−y): his lamp on the strut. */
const iatanUp = 768;

/** The struts loose: neither held by a golem nor wedged. */
export const strutsLoose = (w: StrutsState): number => w.st.filter((x) => x === 0).length;

/** The stair groans (warned) and sheds stone over the whole ledge: a share of the hero's life for each strut loose. */
const groanTick = (s: BattleState, r: StrutsRules, w: StrutsState, cue: (c: string) => void): void => {
  if (w.end >= 0) return;
  const C = r.groan;
  if (w.groanAt < 0 && s.waveT >= w.groan) {
    if (!strutsLoose(w)) {
      w.groan = s.waveT + C.every;
      return;
    }
    w.groanAt = s.tick + C.warn;
    ev(s, { t: 'senda', kind: 'groanWarn', npc: '', x: 0, y: 0, n: C.warn });
    cue('groan');
  } else if (w.groanAt >= 0 && s.tick >= w.groanAt) {
    w.groanAt = -1;
    w.groan = s.waveT + C.every - C.warn;
    const n = strutsLoose(w);
    ev(s, { t: 'senda', kind: 'groan', npc: '', x: 0, y: 0, n });
    if (n > 0) for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, C.bp * n), null, 'physical', true);
  }
};

/** A band of the Marea over the rim: some for the golems, the rest for the houses. */
const foesTick = (s: BattleState, r: StrutsRules, w: StrutsState): void => {
  if (s.waveT < w.foe || w.end >= 0) return;
  w.foe = s.waveT + r.foes.every;
  w.bands++;
  const n = r.foes.n + idiv(s.waveT, r.foes.grow);
  const at = r.rim[rngInt(s.rng, r.rim.length)] as { x: number; y: number };
  for (let k = 0; k < n; k++) {
    const kind = (['goblin', 'wraith', 'skeleton'] as const)[rngInt(s.rng, 3)] as 'goblin' | 'wraith' | 'skeleton';
    const e = spawnEnemy(s, kind, 1, false, 0, at.x + rngRange(s.rng, -400, 400), at.y + rngRange(s.rng, -200, 200));
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
    if (!rngChance(s.rng, r.foes.golemBp)) w.aims.push({ id: e.id, door: rngInt(s.rng, r.doors.length) });
  }
  ev(s, { t: 'senda', kind: 'holeOut', npc: '', x: at.x, y: at.y, n });
};

export const strutsTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.struts;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.struts ??= fresh(r, s));
  if (!s.started) return;
  if (w.pend.length) {
    for (const c of w.pend) cue(c);
    w.pend.length = 0;
  }
  golemsTick(s, r, w, cue);
  bruniTick(s, r, w, cue);
  grilloTick(s, r, w, cue);
  groanTick(s, r, w, cue);
  foesTick(s, r, w);
  // The quarrymen at their doors.
  (['quarry', 'quarry2'] as const).forEach((id, k) => {
    const q = npc(s, id);
    if (q) w.picks[k] = shootTick(s, q, r.pick, w.picks[k] as number);
  });
  // (Gone ones off the list.)
  if (w.aims.length && s.tick % 12 === 0) w.aims = w.aims.filter((a) => s.enemies.some((e) => e.id === a.id && e.hp > 0));
  const h = s.heroes[0];
  if (h && !w.hurt && h.hp * 2 < h.stats.maxHp) {
    w.hurt = true;
    cue('hurt');
  }
};

/**
 * A nightmare going for the houses (from the enemies' tick; true: moved here). With the hero close by, it fights it (the
 * common chase); else it walks to its doorstep, and in.
 */
export const strutsHold = (s: BattleState, e: Enemy, px: number): boolean => {
  const r = s.rules?.senda?.struts;
  const w = s.senda?.struts;
  if (!r || !w || e.hp <= 0) return false;
  const a = w.aims.find((x) => x.id === e.id);
  if (!a) return false;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h && withinRange(h.x, h.y, e.x, e.y, 1500)) return false;
  const d = r.doors[a.door] as { x: number; y: number };
  e.anim = 'walk';
  if (stepToward(e, d.x, d.y, px) || withinRange(e.x, e.y, d.x, d.y, 450)) {
    e.hp = 0;
    w.aims = w.aims.filter((x) => x.id !== e.id);
    w.leaks++;
    ev(s, { t: 'senda', kind: 'houseIn', npc: '', x: d.x, y: d.y, n: w.leaks });
    w.pend.push(`house${w.leaks}`);
  }
  return true;
};

/** The four struts wedged. */
export const strutsGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.struts;
  const need = s.rules?.senda?.struts?.struts.length ?? 4;
  return { done: w ? w.st.filter((x) => x === 2).length : 0, need };
};

/** Won a moment after the last strut is wedged; lost with `houses` nightmares in. */
export const strutsResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.struts;
  const w = s.senda?.struts;
  if (!r || !w) return null;
  if (w.leaks >= r.houses) return 'lost';
  return w.end >= 0 && s.tick >= w.end + 24 ? 'won' : null;
};
